import {
  setCompressedItem,
  getCompressedItem,
} from '@/utils/compressedStorage';
import { SearchResults, Result, Result2 } from '../types/search.types';

type FlattenedResult = { rootId: string } & { [key: string]: unknown };

const ENTITY_TYPE_MAP: Record<string, string> = {
  candidate: 'candidates',
  contact: 'candidates',
  job: 'jobs',
  interview: 'interviews',
  flow: 'flows',
};

const getEntityType = (entity: string): string =>
  ENTITY_TYPE_MAP[entity] ?? entity;

const flattenResults = (results: (Result | Result2)[]): FlattenedResult[] =>
  results.map(({ id, data }) => ({
    ...data,
    rootId: id,
  }));

const MAX_RECENT_SEARCHES = 3;

/**
 * Transforms the API search response into a flat map keyed by entity type
 * (e.g. "candidates", "jobs"), merging primary and alternative results.
 * Each result's nested `data` is spread to the top level with `rootId` preserved.
 */
const serializeSearchResults = (
  searchResults: SearchResults
): Record<string, FlattenedResult[]> => {
  const { entity, results, alternative_results = [] } = searchResults || {};
  return {
    [getEntityType(entity)]: flattenResults(results),
    ...Object.fromEntries(
      alternative_results.map((alt) => [
        getEntityType(alt.entity),
        flattenResults(alt.results as any),
      ])
    ),
    // Flows use a separate API endpoint; always initialize as empty here
    flows: [],
  };
};

const getAISummaryMap = (
  searchResults: SearchResults
): Record<string, string> => {
  const { results } = searchResults;
  const summaryMap = {};
  results.forEach((result) => {
    if (result.ai_summary) {
      summaryMap[result.id] = result.ai_summary;
    }
  });
  return summaryMap;
};

const getLocalSearchHistory = (key: string): string[] => {
  const existing: any[] = getCompressedItem<any[]>(key) || [];
  return existing;
};

const storeLocalSearchHistory = (key: string, value: any) => {
  const existing: any[] = getCompressedItem<any[]>(key) || [];
  const filtered = existing.filter(
    (q: any) => JSON.stringify(q) !== JSON.stringify(value)
  );
  const updated = [value, ...filtered].slice(0, MAX_RECENT_SEARCHES);
  setCompressedItem(key, updated);
};

export {
  serializeSearchResults,
  getLocalSearchHistory,
  storeLocalSearchHistory,
  getAISummaryMap,
};
